import { Button } from '@mm/components';
import { PlusCircleIcon } from '@mm/icons';
import AttributeModel from '@mm/state/model/AttributeModel';
import { FormEntry } from '@mm/state/model/FormsModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { selectEnv } from '@mm/state/selectors/envSelectors';
import classnames from 'classnames';
import React, { KeyboardEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { colors } from '../../../editor/features/beta/styles/colors';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import { useAttributeHandlers } from '../../helpers/widget-helpers/use-attribute-handlers';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { nameById } from '@mm/state/helpers/filterable-select-helpers';
import PrimarySelect, { DropdownOption } from '../PrimarySelect/PrimarySelect';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import TextInput from '../TextInput';
import './SingleAttributeInput.pcss';

type Props = {
  widget: WidgetModel;
  qqResponseType?: string | null;
  className?: string;
  formEntry?: FormEntry;
  includeSettableSystemVars?: boolean;
  showErrorMessage?: boolean;
  usedInSlideout?: boolean;
};

const SingleAttributeInput = (props: Props) => {
  const {
    widget,
    className,
    formEntry,
    qqResponseType,
    showErrorMessage,
    usedInSlideout,
  } = props;
  const variableId = formEntry
    ? formEntry.variableId
    : widget.config.variableId;
  const variableType = formEntry
    ? formEntry.variableType
    : widget.config.variableType;
  const {
    settableAttributes,
    updateAttribute,
    createAttributeAndUpdateWidget,
  } = useAttributeHandlers(true, true, false);
  const savedAttr = settableAttributes.find(
    attr =>
      attr.id === variableId &&
      (variableType === 'SystemVariable') === !!attr.system,
  );
  const env = useSelector(selectEnv);
  const [viewCreateAttribute, setViewCreateAttribute] = useState(false);
  const [attributeInput, setAttributeInput] = useState('');
  const classList = classnames('single-attribute-input', className);

  const createAndSaveNewAttribute = async () => {
    await createAttributeAndUpdateWidget(widget, attributeInput, formEntry);
    setViewCreateAttribute(false);
  };

  const onAttrKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ENTER_KEY_NAME) {
      createAndSaveNewAttribute();
    }
  };

  const onClickAddNewAttribute = () => {
    setAttributeInput('');
    setViewCreateAttribute(true);
  };

  const onAttributeSelected = (option: AttributeModel) => {
    setAttributeInput(nameById(option.id, settableAttributes, '', true));
    updateAttribute(widget, option, formEntry);
  };

  const customOptionRenderer = (option: DropdownOption) => {
    const attr = option as AttributeModel;
    const attributeName = attr.name.replace(' ', '_');

    const warningText = Object.entries(env.sysVariablesAllowedToAssign)
      .filter(([_, attrs]) => attrs.includes(attributeName))
      .map(([channel]) => channel.toLowerCase())
      .join(', ');

    if (attr.system) {
      return (
        <>
          {attr.name}{' '}
          {warningText && (
            <span className="single-attribute-input__attribute-annotation">
              ({warningText} only)
            </span>
          )}
        </>
      );
    }
    return attr.name;
  };

  if (viewCreateAttribute) {
    return (
      <div className={classList}>
        <TextInput
          className="widget-common__input widget-common__input--attr chatbot__input"
          value={attributeInput}
          maxLength={30}
          onChange={e => setAttributeInput(e.target.value)}
          placeholder="Enter attribute name"
          onKeyUp={onAttrKeyUp}
        />
        <PrimaryButton
          onClick={() => {
            setAttributeInput('');
            setViewCreateAttribute(false);
          }}
          className="single-attribute-input__attr-button"
          color={ButtonColor.secondaryBlue}
        >
          Cancel
        </PrimaryButton>
        <PrimaryButton
          onClick={createAndSaveNewAttribute}
          className="single-attribute-input__attr-button"
        >
          Save
        </PrimaryButton>
      </div>
    );
  }

  return (
    <div className={classList}>
      <PrimarySelect
        className={classnames('single-attribute-input__select', {
          'single-attribute-input__select--empty': !savedAttr,
        })}
        options={settableAttributes}
        value={savedAttr}
        onChange={onAttributeSelected}
        showSearchFilter={true}
        dropdownClassName="single-attribute-input__dropdown-wrapper"
        customOptionRenderer={customOptionRenderer}
        customDropdownStyles={{
          zIndex: usedInSlideout ? '1000003' : '999',
        }}
      />
      {showErrorMessage && !savedAttr && (
        <ErrorMessage
          displayType="triangle"
          error="Required"
          className="single-attribute-input__error"
        />
      )}

      <Button
        css={{ container: 'single-attribute-input__add-new-button' }}
        color="transparent-no-shadow"
        size="sm"
        onClick={onClickAddNewAttribute}
        leftIcon={<PlusCircleIcon color={colors.accentBlue} />}
      >
        New attribute
      </Button>
    </div>
  );
};

export default SingleAttributeInput;
